import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { ArrowUpRight, ImageIcon, X } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CroppedImage } from "@/components/waste/cropped-image";
import { ImageLightbox } from "@/components/waste/image-lightbox";
import type { WasteEvent } from "@/hooks/use-waste-events";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import type { Lens } from "@/hooks/use-lens";
import { useProofEvents, useProofCount } from "@/hooks/use-proof-events";
import { previousPeriod, selectProofEvents, type ProofFilter } from "@/lib/proof";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatDate, formatNumber, formatWeight } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";

type Range = { from: string | null; to: string | null };

const CARD_COUNT = 4;

const SERVICE_KEYS: Record<string, string> = {
  breakfast: "reference.serviceTime.breakfast",
  lunch: "reference.serviceTime.lunch",
  dinner: "reference.serviceTime.dinner",
  unassigned: "—",
};

// waste_type → the reason tag on each card, reusing the design-system status
// colours. Covers every waste_type value.
/** Literal keys in a lookup table — a key built at runtime is invisible to `check-i18n.mjs`. */
const WASTE_TYPE_TAG: Record<string, { labelKey: string; cls: string }> = {
  overproduction: {
    labelKey: "reference.wasteType.overproduction",
    cls: "bg-orange-50 text-orange-700",
  },
  spoilage: { labelKey: "reference.wasteType.spoilage", cls: "bg-red-50 text-red-700" },
  preparation: { labelKey: "reference.wasteType.preparation", cls: "bg-amber-50 text-amber-700" },
  post_consumer: { labelKey: "reference.wasteType.post_consumer", cls: "bg-blue-50 text-blue-700" },
  packaging: { labelKey: "reference.wasteType.packaging", cls: "bg-purple-50 text-purple-700" },
  unassigned: { labelKey: "reference.wasteType.unassigned", cls: "bg-gray-100 text-gray-600" },
};

type Props = {
  filters: WasteFilters;
  dateRange: Range;
  lens: Lens;
  currency: string;
  filter: ProofFilter | null;
  onClearFilter: () => void;
};

export function ProofWidget({ filters, dateRange, lens, currency, filter, onClearFilter }: Props) {
  const { locale, t } = useTranslation();
  const { search } = useLocation();
  const eventsQuery = useProofEvents(filters, dateRange);
  const prevRange = useMemo(() => previousPeriod(dateRange, new Date()), [dateRange]);
  const prevCountQuery = useProofCount(filters, prevRange);
  const [lightboxEvent, setLightboxEvent] = useState<WasteEvent | null>(null);

  const total = eventsQuery.data?.total ?? 0;
  const cards = useMemo(
    () => selectProofEvents(eventsQuery.data?.events ?? [], { filter, limit: CARD_COUNT }),
    [eventsQuery.data, filter],
  );

  // Scroll into view when a feed filter lands (Pulse/heatmap link-in, POS2-871/873).
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (filter) panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [filter]);

  const isLoading = eventsQuery.isLoading && eventsQuery.data == null;

  return (
    <section
      ref={panelRef}
      id="proof-panel"
      className="rounded-xl border border-gray-200/80 bg-white px-4 py-4 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]"
      data-testid="proof-widget"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-sm font-bold tracking-tight text-gray-900">
            {t("dashboard.proofTitle")}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">{t("dashboard.proofDescription")}</p>
        </div>
        {filter ? (
          <button
            type="button"
            onClick={onClearFilter}
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
            data-testid="proof-filter-chip"
          >
            {/* Translated at render, not where the filter is set: `ProofFilter.label` stays the
                stored name that pairs with `key`, so a language switch re-reads the chip
                (POS2-1158). */}
            {foodLabel(t, filter.label)}
            <X className="size-3" aria-hidden />
          </button>
        ) : (
          <p className="shrink-0 text-xs text-gray-500" data-testid="proof-capture-count">
            <b className="font-semibold text-gray-900 tabular-nums">
              {formatNumber(locale, total)}
            </b>{" "}
            {t("dashboard.proofCaptures")}
          </p>
        )}
      </div>

      {isLoading ? (
        <ProofSkeleton />
      ) : cards.length === 0 ? (
        <ProofEmpty previousCount={prevCountQuery.data ?? 0} />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map((event) => (
            <ProofCard
              key={event.id}
              event={event}
              lens={lens}
              currency={currency}
              onExpand={() => setLightboxEvent(event)}
            />
          ))}
        </div>
      )}

      <Link
        to={{ pathname: "/waste/transactions", search }}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700"
        data-testid="proof-view-all"
      >
        {t("dashboard.proofViewAll")}
        <ArrowUpRight className="size-3.5" aria-hidden />
      </Link>

      <ImageLightbox
        open={lightboxEvent != null}
        onOpenChange={(open) => !open && setLightboxEvent(null)}
        imageUrl={lightboxEvent?.image_read_url ?? null}
        beforeImageUrl={lightboxEvent?.before_image_read_url ?? null}
        eventId={lightboxEvent?.id ?? null}
        boundingArea={lightboxEvent?.bounding_area ?? null}
      />
    </section>
  );
}

function ProofCard({
  event,
  lens,
  currency,
  onExpand,
}: {
  event: WasteEvent;
  lens: Lens;
  currency: string;
  onExpand: () => void;
}) {
  const { locale, t } = useTranslation();
  const tag = event.waste_type ? WASTE_TYPE_TAG[event.waste_type] : null;
  const cost = formatCost(locale, event.cost_estimate, currency);
  const weight = formatWeight(locale, event.item_weight, 2);
  const primary = lens === "weight" ? weight : (cost ?? "—");
  const secondary = lens === "weight" ? cost : weight;
  const serviceKey = event.service_time ? SERVICE_KEYS[event.service_time] : null;
  const service = serviceKey ? t(serviceKey) : (event.service_time ?? null);

  return (
    <article
      className="flex flex-col overflow-hidden rounded-lg border border-black/[0.06] bg-white"
      data-testid="proof-card"
      data-event-id={event.id}
    >
      <button
        type="button"
        onClick={onExpand}
        disabled={!event.image_read_url}
        className="relative aspect-[4/3] w-full overflow-hidden bg-gray-50 transition-opacity hover:opacity-95"
        aria-label={t("dashboard.proofExpandPhoto")}
      >
        {event.image_read_url ? (
          <CroppedImage
            src={event.image_read_url}
            alt={t("dashboard.proofCaptureAlt", { id: event.id })}
            box={event.bounding_area}
            fill
            className="size-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-300">
            <ImageIcon className="size-6" aria-hidden />
          </div>
        )}
        {tag && (
          <span
            className={cn(
              "absolute left-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
              tag.cls,
            )}
          >
            {t(tag.labelKey)}
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <p className="truncate text-xs font-medium text-gray-900" title={eventLabel(t, event)}>
          {eventLabel(t, event)}
        </p>
        <p className="truncate text-[11px] text-gray-400">
          {[event.kitchen_name, service, formatDay(locale, event.logged_at, event.timezone)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <div className="mt-auto flex items-end justify-end pt-2">
          <span className="text-right">
            <span className="block text-sm font-bold tabular-nums text-gray-900">{primary}</span>
            {secondary && (
              <span className="block text-[10px] tabular-nums text-gray-400">{secondary}</span>
            )}
          </span>
        </div>
      </div>
    </article>
  );
}

function ProofEmpty({ previousCount }: { previousCount: number }) {
  const { locale, t } = useTranslation();
  return (
    <div
      className="rounded-lg border border-black/[0.06] bg-gray-50/60 px-4 py-8 text-center text-sm text-gray-500"
      data-testid="proof-empty"
    >
      {/* Green headline: 0 captures for a watched line means the problem is
          gone — the empty state is itself the proof (design mock §6). */}
      <b className="font-semibold text-green-700">{t("dashboard.proofZeroCaptures")}</b> ·{" "}
      {t("dashboard.proofPrevCaptures", { count: formatNumber(locale, previousCount) })}
    </div>
  );
}

function ProofSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="proof-skeleton">
      {Array.from({ length: CARD_COUNT }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-black/[0.06]">
          <Skeleton className="aspect-[4/3] w-full rounded-none" />
          <div className="flex flex-col gap-1.5 p-2.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2.5 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

/** The card's item label — most specific known name, mirroring the card title
 * logic in TransactionCard without the two-line category split. */
function eventLabel(t: TFunction, event: WasteEvent): string {
  return (
    foodLabel(t, event.inventory_item_name) ??
    foodLabel(t, event.item?.label) ??
    foodLabel(t, event.subcategory_name) ??
    foodLabel(t, event.category_name) ??
    t("dashboard.proofUnidentified")
  );
}

function formatCost(locale: Locale, cost: number | null, currency: string): string | null {
  if (cost == null) return null;
  return formatCurrency(locale, cost, currency);
}

function formatDay(locale: Locale, loggedAt: string, timezone: string | null): string {
  return formatDate(locale, loggedAt, {
    day: "numeric",
    month: "short",
    timeZone: timezone ?? "UTC",
  });
}
